import ReusableModal from '@components/Modal/ReusableModal.component'
import { ArrowForward, AutoFixHighOutlined, CheckBox, CheckOutlined, CloseOutlined, RadioButtonChecked, RadioButtonCheckedOutlined, RadioButtonUnchecked, RadioButtonUncheckedOutlined, TextsmsOutlined } from '@mui/icons-material'
import { Box, Button, Divider, Slide, Slider, styled, TextareaAutosize, Typography } from '@mui/material'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import reviewStore from 'store/reviewStore'
import interviewAssistance from 'assets/images/interview-assistance.png'

export const Route = createFileRoute('/(partner)/_partner/review-mode')({
  component: RouteComponent,
})

const CustomSlider = styled(Slider)({
    height: '8px',
  
    '& .MuiSlider-rail': {
      height: '8px',
      backgroundColor: 'var(--Light-Gray)',
      borderRadius: '4px',
    },

    '& .MuiSlider-track': {
      height: '8px',
      border: 'none',
      borderRadius: '4px',
      background: 'linear-gradient(90deg, #31C07D 0%, #47C5FB 35%, #9747FF 75%, #EB4335 100%)',
    },

    '& .MuiSlider-thumb': {
        height: '24px',
        width: '24px',
        backgroundColor: '#EB4335',
        boxShadow: '0px 2px 4px 0px #02051140',
        // border: 'none',
        // position: 'relative',

        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '9.5px',
            height: '9.5px',
            borderRadius: '50%',
            backgroundColor: '#fff',
        },

        '&:hover': {
            boxShadow: '0px 2px 8px rgba(235, 67, 53, 0.4)',
        },
    },
})

const StyledTextArea = styled(TextareaAutosize)(() => ({
    width: '100%',
    minHeight: '100px',
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--Light-Gray)',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    resize: 'vertical',
    '&:focus': {
        outline: 'none',
    },
}));

function RouteComponent() {

    const queryClient = useQueryClient();
    const {reviewData} = reviewStore();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [openModal, setOpenModal] = useState(false)
    const [comment, setComment] = useState('')
    const [rate, setRate] = useState(0)

    const MAX = 100;
    const MIN = 0;

    useEffect(() => {
        console.log('rate', rate);
    }, [rate])


    //Prikupljanje kategorija
    const {data: allCategories} = useQuery({
        queryKey: ['review-all-categories'],
        queryFn: async () => {
            const token = localStorage.getItem('token');
            const response = await axios.get(`https://interviewer.runasp.net/api/submittedInterviews/${reviewData.candidateId}/submittedCategories`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            })
            return response.data
        },
        enabled: !!reviewData.candidateId
    })

    //Prikupljanje pitanja unutar jedne kategorije
    const categoryQuestions = useQueries({
        queries: (allCategories ?? []).map(cat => ({
            queryKey: ['review-category-questions', cat.id], 
            queryFn: async () => {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `https://interviewer.runasp.net/api/submittedInterviews/${reviewData.candidateId}/submittedCategories/${cat.id}/submittedQuestions/review`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                return response.data;
            },
            enabled: !!allCategories // pokreće se tek kada kategorije stignu
        }))
    });


    // Spajanje svih pitanja u niz i dodavanje parametra id kategorije za svako pitanje
    const allQuestions = categoryQuestions
    .map((q, index) => {
        const categoryId = allCategories[index]?.id;
        return (q.data ?? []).map(question => ({
            ...question,
            categoryId  // dodamo kategoriju tom pitanju
        }));
    })
    .flat();

    const currentQuestion = allQuestions[currentIndex];


    // Ocenjivanje pitanja (pojedinacno)
    const handleReviewClick = (value:boolean, id:string, catId) => {
        const payload = {
            "comment" : comment,
            "isCorrect": value,
            "score": rate
        }
        submitQuestionMutation.mutate({ payload, id, catId })
    }

    const submitQuestionMutation = useMutation({
        mutationFn: async ({ payload, id, catId }: { payload: { comment: string; isCorrect: boolean; score: number }, id: string, catId:string }) => {
            const token = localStorage.getItem('token');
            const response = await axios.put(`https://interviewer.runasp.net/api/submittedInterviews/${reviewData.candidateId}/submittedCategories/${catId}/submittedQuestions/${id}/review`, payload, {
                headers:  {Authorization: `Bearer ${token}`},
            })
            return response
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['review-category-questions', variables.catId] });
            toast.success('Successfully graded question');
        },
        onError: (error) => {
            console.log(error);
        }
    })

  return (
    <Box sx={{
        paddingX: '125px',
        paddingY: '32px'
    }}>
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        }}>
            <Button 
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex(i => i - 1)}
                sx={{
                    color: 'var(--Interviewer-Green)',
                    fontWeight: '600'
                }}>
                    {'< Back'}
            </Button>
            <Typography sx={{color: 'var(--Interviewer-Green)', fontWeight: '600'}}>{currentIndex+1}</Typography>
            <Button
                disabled={currentIndex === allQuestions.length - 1}
                onClick={() => setCurrentIndex(i => i + 1)}
                sx={{
                    color: 'var(--Interviewer-Green)',
                    fontWeight: '600'
                }}>
                {'Next >'}
            </Button>
        </Box>

        <Divider sx={{mt: '16px'}} />

        <Box sx={{mt: '32px'}}>
            <Typography variant='h1'>Question {currentIndex+1}</Typography>


            {currentQuestion?.structure === 3 ? (
                <Typography sx={{py: '16px', fontWeight: '500', fontSize: '16px'}}>{currentQuestion?.text}</Typography>
            ) : (
                <Box sx={{py: '16px'}}>
                    <Typography sx={{fontWeight: '500', fontSize: '16px'}}>{currentQuestion?.text}</Typography>

                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '10px', pt: '40px'}}>
                        {currentQuestion?.comparationAnswers.map((question, index) => (
                            <Box
                                key={index} 
                                sx={(theme) => ({
                                    padding: '24px',
                                    borderRadius: '16px',
                                    border: question?.isCorrect ? '2px solid var(--Interviewer-Green)' : '1px solid var(--Light-Gray)',
                                    boxShadow: '0px 1px 2px 0px #0205111A',
                                    background: question?.isCorrect ? 'rgba(49, 192, 125, 0.04)' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                            })}>
                                <CheckBox fontSize='small'/>
                                {question?.answer}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Odgovor kandidata */}
            <>
                <Box sx={{
                    backgroundColor: '#fff',
                    border: '1px solid var(--Light-Gray)',
                    borderRadius: '12px',
                    boxShadow: '0px 1px 2px 0px #0205111A'
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', md: 'row'},
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '24px',
                    }}>
                        <Typography variant='h2'>Candidate Answer</Typography>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: {xs: 'column', md: 'row'},
                            alignItems: 'center',
                            gap: '18px'
                        }}>
                            <Button
                                variant="outlined"
                                onClick={() => setOpenModal(true)}
                                sx={{
                                    display: 'flex',
                                    gap: '10px',
                                    border: '1px solid var(--Interviewer-Near-White)'
                                }}>
                                <TextsmsOutlined fontSize='small'/>
                                Leave a comment
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{
                                    display: 'flex',
                                    gap: '10px',
                                    border: '1px solid var(--Light-Gray)',
                                    background: 'var(--Light-Gray)'
                                }}>
                                <img src={interviewAssistance} style={{ width: '25px', height: '25px' }} />
                                Interviewer Assistance
                            </Button>
                        </Box>
                    </Box>
                    <Divider />

                    {currentQuestion?.structure === 3 ? (
                        <Typography sx={{
                            padding: '24px',
                            fontSize: '16px',
                            fontWeight: '500'
                        }}>
                            Lorem ipsum dolor sit amet consectetur adipisicing elit. Dignissimos quas dolorum incidunt dolor adipisci nulla nam, nesciunt molestiae numquam soluta.
                        </Typography>
                    ) : (
                        <Box sx={{
                            padding: '24px'
                        }}>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                {currentQuestion?.comparationAnswers.map((question, index) => (
                                    <Box
                                        key={index} 
                                        sx={(theme) => ({
                                            padding: '24px',
                                            borderRadius: '16px',
                                            border: question?.isPicked ? '2px solid var(--Interviewer-Green)' : '1px solid var(--Light-Gray)',
                                            boxShadow: '0px 1px 2px 0px #0205111A',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px'
                                            // background: question?.isPicked ? 'rgba(49, 192, 125, 0.04)' : 'none',
                                    })}>
                                        <RadioButtonChecked fontSize='small'/>
                                        {question?.answer}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
             </>

            {/* Predlozeni odgovor samo ako je tekstualno pitanje bilo */}
            <>
                {currentQuestion?.structure === 3 && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        backgroundColor: 'var(--Light-Gray)',
                        border: '1px solid var(--Light-Gray)',
                        borderRadius: '12px',
                        boxShadow: '0px 1px 2px 0px #0205111A',
                        mt: '16px',
                        padding: '24px'
                    }}>
                        <Typography variant='h2'>Proposed Answer</Typography>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: '500'
                        }}>Lorem ipsum dolor sit amet consectetur adipisicing elit. Cupiditate inventore vitae voluptatibus nulla voluptatem magni dolorum sint delectus ad? Cupiditate!</Typography>
                    </Box>
                )}
            </>
        </Box>

        <Divider sx={{mt: '32px'}}/>


        {/* Rate Candidate */}
        <Box sx={{
            padding: '32px'
        }}>
            <Typography variant='h2'>Rate candidate answer</Typography>

            <CustomSlider
                step={10}
                value={rate}
                valueLabelDisplay="auto"
                min={MIN}
                max={MAX}
                marks={[
                    {
                        value: MIN,
                        label: 'Bad',
                    },
                    {
                        value: MAX,
                        label: 'Excellent',
                    },
                ]}
                onChange={(e, newValue) => setRate(newValue)}
            />
        </Box>

        {/* Dugmad */}
        <Box sx={{
            display: 'flex',
            flexDirection: {xs: 'column', md: 'row'},
            gap: '20px'
        }}>
            <Box sx={(theme) => ({
                border: currentQuestion?.isCorrect ? '3px solid var(--Interviewer-Green)' : 'none',
                borderRadius: '16px',
                width: '100%',
                py: '4px', px: '4px',
                minHeight: '60px',
                maxHeight: '80px',
                '&:hover': {
                    border: currentQuestion?.isCorrect ? '3px solid var(--Interviewer-Green)' : 'none',
                },
            })}>
                <Button
                    variant="text"
                    disabled={currentQuestion?.isCorrect}
                    onClick={() => handleReviewClick(true, currentQuestion.id, currentQuestion.categoryId)}
                    sx={(theme) => ({
                        width: '100%',
                        height: '100%',
                        background: 'var(--Interviewer-Green)',
                        border: '1px solid var(--Interviewer-Green)',
                        opacity: currentQuestion?.isCorrect ? 0.5 : 1,
                    })}
                >
                <Typography variant='h2' sx={{color: '#fff'}}>Mark as correct</Typography>
                <CheckOutlined fontSize='small' sx={{color: '#fff'}}/>
                </Button>
            </Box>

            <Box sx={(theme) => ({
                border: !currentQuestion?.isCorrect ? '3px solid #EB4335' : 'none',
                borderRadius: '16px',
                width: '100%',
                py: '4px', px: '4px',
                minHeight: '60px',
                maxHeight: '80px',
                '&:hover': {
                    border: !currentQuestion?.isCorrect ? '3px solid var(--Red-Error-Color)' : 'none',
                },
            })}>
                <Button 
                    variant="text"
                    disabled={!currentQuestion?.isCorrect}
                    onClick={() => handleReviewClick(false, currentQuestion.id, currentQuestion.categoryId)}
                    sx={(theme) => ({
                        width: '100%',
                        height: '100%',
                        background: 'var(--Red-Error-Color)',
                        border: '1px solid var(--Red-Error-Color)',
                        opacity: !currentQuestion?.isCorrect ? 0.5 : 1,
                    })}
                >
                <Typography variant='h2' sx={{color: '#fff'}}>Mark as incorrect</Typography>
                <CloseOutlined fontSize='small' sx={{color: '#fff'}} />
                </Button>
            </Box>

        </Box>

        <Divider sx={{ my: 3 }} textAlign="center">or</Divider>

        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px'}}>
            <Typography sx={{color: 'var(--Interviewer-Lighter-Black)'}}>Skip question</Typography>
            <ArrowForward fontSize='small' sx={{width: '16px', height: '12px'}}/>
        </Box>


        {/* Modal */}
        <ReusableModal onClose={() => setOpenModal(false)} open={openModal}>
            <Box>
                <StyledTextArea 
                    placeholder="Add your comment here" 
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </Box>
        </ReusableModal>
    </Box>
  )
}
